import React, { useMemo, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../shared/ui";
import { useRoles } from "../../../shared/api/hooks/useRoles";
import { useAdmins } from "../../../shared/api/hooks/useAdmins";

export interface EscalateModalSubmitData {
  adminId: string;
  role: string;
  note: string;
}

interface EscalateModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: EscalateModalSubmitData) => void;
  customerName?: string;
  isSubmitting?: boolean;
}

export const EscalateModal: React.FC<EscalateModalProps> = ({
  open,
  onClose,
  onConfirm,
  customerName,
  isSubmitting = false,
}) => {
  const [escalateType, setEscalateType] = useState<"role" | "member">("role");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedRoleName, setSelectedRoleName] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [selectedMemberName, setSelectedMemberName] = useState("");
  const [note, setNote] = useState("");

  const currentUser = useSelector((state: any) => state.users || state.auth?.users);
  const { roles, isLoading: isLoadingRoles } = useRoles();
  const { admins, isLoading: isLoadingAdmins } = useAdmins({
    isProfileCompleted: true,
  });

  const roleList = useMemo(() => {
    if (roles && roles.length > 0) {
      return roles.map((r) => ({
        id: r.id,
        name: r.name,
        admins: r.admins || [],
      }));
    }
    return [];
  }, [roles]);

  const memberList = useMemo(() => {
    if (admins && admins.length > 0) {
      return admins.map((a) => {
        const roleName = a.role || a.roles?.[0]?.name || "Finance manager";
        return {
          id: a._id,
          name: `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.email,
          email: a.email,
          role: roleName,
        };
      });
    }
    return [];
  }, [admins]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
    setSelectedRoleId("");
    setSelectedRoleName("");
    setSelectedMemberId("");
    setSelectedMemberName("");
    setNote("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    let targetAdminId = "";
    let targetRole = "";

    if (escalateType === "role") {
      if (!selectedRoleName) {
        toast.error("Please select a role to escalate to");
        return;
      }
      targetRole = selectedRoleName;

      const matchingRole = roleList.find(
        (r) => r.name === selectedRoleName || r.id === selectedRoleId
      );
      const roleAdminId = matchingRole?.admins?.[0]?._id;
      const matchingAdminRecord = admins?.find(
        (a) =>
          a.role === selectedRoleName ||
          a.roles?.some(
            (r) => r.name === selectedRoleName || r._id === selectedRoleId
          )
      )?._id;

      targetAdminId =
        roleAdminId ||
        matchingAdminRecord ||
        currentUser?._id ||
        selectedRoleId ||
        "";
    } else {
      if (!selectedMemberId) {
        toast.error("Please select a team member to escalate to");
        return;
      }
      targetAdminId = selectedMemberId;
      const member = memberList.find((m) => m.id === selectedMemberId);
      targetRole = member?.role || "Finance manager";
    }

    onConfirm({
      adminId: targetAdminId,
      role: targetRole,
      note: note.trim() || "Please review this OTC request.",
    });
  };

  const hasSelection =
    escalateType === "role" ? Boolean(selectedRoleName) : Boolean(selectedMemberId);

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[520px] bg-white rounded-[24px]"
    >
      <ModalHeader className="items-center justify-between pb-3 border-b border-[#EBEDF9]">
        <ModalTitle className="text-[20px] font-[600] text-[#161827]">
          Escalate to...
        </ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          onClick={handleClose}
          className="rounded-full p-2 text-[#161827] hover:bg-[#F2F4FF] transition-colors"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <form onSubmit={handleSubmit}>
        <ModalBody className="flex flex-col gap-6 py-6">
          {customerName && (
            <p className="text-[13px] text-[#6F739B]">
              Escalate this OTC request for{" "}
              <strong className="text-[#161827]">{customerName}</strong> to internal compliance or treasury review.
            </p>
          )}

          {/* Escalate to... Radio */}
          <div className="flex flex-col gap-2.5">
            <p className="text-[14px] font-[450] text-[#2C2F44]">
              Escalate to...
            </p>
            <div className="flex items-center gap-8">
              {(["role", "member"] as const).map((type) => {
                const isActive = escalateType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setEscalateType(type)}
                    className="inline-flex items-center gap-3 text-[14px] font-[450] text-[#2C2F44] transition-colors focus:outline-none"
                  >
                    <span
                      className={`grid h-5 w-5 place-items-center rounded-full border transition-colors ${
                        isActive ? "border-[#6F00FF]" : "border-[#C7CEE4]"
                      }`}
                    >
                      {isActive ? (
                        <span className="h-2.5 w-2.5 rounded-full bg-[#6F00FF]" />
                      ) : null}
                    </span>
                    <span className="capitalize">{type}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Role / Member Selection */}
          <div className="flex flex-col gap-2">
            <p className="text-[14px] font-[450] text-[#2C2F44] capitalize">
              {escalateType}
            </p>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="flex h-[48px] w-full items-center justify-between rounded-[8px] bg-[#E9ECF8] px-4 text-left transition-colors hover:bg-[#E2E6F5] focus:outline-none"
                >
                  <span
                    className={`text-[14px] font-[400] truncate ${
                      (escalateType === "role" ? selectedRoleName : selectedMemberName)
                        ? "text-[#161827]"
                        : "text-[#737AA8]"
                    }`}
                  >
                    {escalateType === "role"
                      ? selectedRoleName || (isLoadingRoles ? "Loading roles..." : "Select role")
                      : selectedMemberName || (isLoadingAdmins ? "Loading members..." : "Select member")}
                  </span>
                  <ChevronDown size={18} className="text-[#6F739B] shrink-0" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="start"
                  sideOffset={6}
                  className="z-[125] max-h-[240px] w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-[12px] border border-[#E1E6FF] bg-white p-1.5 shadow-lg"
                >
                  {escalateType === "role" ? (
                    roleList.length ? (
                      roleList.map((r) => {
                        const isSelected = r.name === selectedRoleName;
                        return (
                          <DropdownMenu.Item
                            key={r.id}
                            onSelect={() => {
                              setSelectedRoleId(r.id);
                              setSelectedRoleName(r.name);
                            }}
                            className={`flex items-center cursor-pointer rounded-[8px] px-3.5 py-2.5 text-[14px] outline-none transition-colors focus:bg-[#F5F6FD] ${
                              isSelected
                                ? "bg-[#F1ECFF] text-[#5E01D6] font-[500]"
                                : "text-[#383B56]"
                            }`}
                          >
                            <span className="truncate">{r.name}</span>
                          </DropdownMenu.Item>
                        );
                      })
                    ) : (
                      <div className="px-3.5 py-2 text-[13px] text-[#6F739B]">
                        {isLoadingRoles ? "Loading roles..." : "No roles found."}
                      </div>
                    )
                  ) : memberList.length ? (
                    memberList.map((m) => {
                      const isSelected = m.name === selectedMemberName;
                      return (
                        <DropdownMenu.Item
                          key={m.id}
                          onSelect={() => {
                            setSelectedMemberId(m.id);
                            setSelectedMemberName(m.name);
                          }}
                          className={`cursor-pointer rounded-[8px] px-3.5 py-2 outline-none transition-colors focus:bg-[#F5F6FD] ${
                            isSelected
                              ? "bg-[#F1ECFF] text-[#5E01D6] font-[500]"
                              : "text-[#383B56]"
                          }`}
                        >
                          <div className="text-[14px] font-[500] text-[#161827]">
                            {m.name}
                          </div>
                          {m.email && (
                            <div className="text-[12px] text-[#6F739B]">
                              {m.email}
                            </div>
                          )}
                        </DropdownMenu.Item>
                      );
                    })
                  ) : (
                    <div className="px-3.5 py-2 text-[13px] text-[#6F739B]">
                      {isLoadingAdmins ? "Loading members..." : "No members found."}
                    </div>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>

          {/* Note • Optional */}
          <div className="flex flex-col gap-2">
            <p className="text-[14px] font-[450] text-[#2C2F44]">
              Note <span className="font-normal italic text-[#6F739B]">• Optional</span>
            </p>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Please review this OTC request."
              className="min-h-[100px] w-full resize-none rounded-[8px] bg-[#E9ECF8] p-4 text-[14px] text-[#161827] placeholder:text-[#9A9CC0] focus:outline-none focus:ring-2 focus:ring-[#D9DEFF]"
            />
          </div>
        </ModalBody>

        <ModalFooter className="flex items-center justify-end gap-3 border-t border-[#EBEDF9] px-6 py-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="h-10 rounded-[8px] px-4 py-2 text-[14px] font-medium text-[#344054] hover:bg-[#F5F6FD] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <Button
            type="submit"
            disabled={!hasSelection || isSubmitting}
            className="px-6"
          >
            {isSubmitting ? "Escalating..." : "Escalate"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
